import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useBlog } from '../../contexts/BlogContext';
import { blogService } from '../../services/blogService';
import { Calendar, Clock, ArrowRight, Tag, ChevronLeft, ChevronRight } from 'lucide-react';

const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const postsPerPage = 10;
  
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get page from URL query params
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const page = parseInt(queryParams.get('page')) || 1;
    setCurrentPage(page);
  }, [location.search]);
  
  // Fetch posts with pagination
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const response = await blogService.getPosts({
          page: currentPage,
          per_page: postsPerPage
        });
        
        console.log('BlogList response:', response);
        
        // Handle Laravel pagination response structure
        // Response structure: { success: true, data: { current_page, data: [...], last_page, total, ... } }
        if (response.data && response.data.data) {
          // Laravel pagination response
          setPosts(Array.isArray(response.data.data) ? response.data.data : []);
          setTotalPages(response.data.last_page || 1);
          setTotalPosts(response.data.total || 0);
        } else if (response.data && Array.isArray(response.data)) {
          // Simple array response
          setPosts(response.data);
          setTotalPages(Math.ceil(response.data.length / postsPerPage));
          setTotalPosts(response.data.length);
        } else if (Array.isArray(response)) {
          // Direct array response
          setPosts(response);
          setTotalPages(Math.ceil(response.length / postsPerPage));
          setTotalPosts(response.length);
        } else {
          console.warn('Unexpected response format:', response);
          setPosts([]);
        }
      } catch (error) {
        console.error('Error fetching posts:', error);
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPosts();
  }, [currentPage]);
  
  // Handle page change
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    navigate(`/blog?page=${page}`);
    window.scrollTo(0, 0);
  };

  return (
    <>
      <Helmet>
        <title>Blog - Web Development & Digital Marketing Tips | Karni Digital Solutions Pattaya</title>
        <meta name="description" content="Expert insights on web development, digital marketing, and business growth in Pattaya & Thailand. Latest tips, guides, and industry news from Karni Digital Solutions." />
        <meta name="keywords" content="web development blog, digital marketing tips, Pattaya business blog, Thailand tech blog, website development guide" />
        <link rel="canonical" href="https://karnidigitalsolutions.com/blog" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Karni Digital Solutions Blog",
            "description": "Expert insights on web development, digital marketing, and business growth in Pattaya & Thailand",
            "url": "https://karnidigitalsolutions.com/blog",
            "publisher": {
              "@type": "Organization",
              "name": "Karni Digital Solutions",
              "logo": {
                "@type": "ImageObject",
                "url": "https://karnidigitalsolutions.com/logo.png"
              }
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-primary py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-20 h-20 border border-gold rounded-full"></div>
          <div className="absolute bottom-10 right-20 w-16 h-16 bg-gold rounded-full"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6" data-aos="fade-up">
              Latest Insights & Tips
            </h1>
            <p className="text-xl md:text-2xl text-gold max-w-3xl mx-auto" data-aos="fade-up" data-aos-delay="200">
              Expert advice on web development, digital marketing & business growth in Pattaya & Thailand
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <div className="h-8 w-1.5 bg-primary rounded-full"></div>
              Latest Articles
              <span className="text-base font-normal text-gray-500">({totalPosts} posts)</span>
            </h2>
            <div className="flex gap-2">
              <button 
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg border transition-colors ${viewMode === 'list' ? 'border-primary bg-primary/10 text-primary' : 'border-gray-300 hover:bg-gray-100 text-gray-600'}`}
                title="List view"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                </svg>
              </button>
              <button 
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg border transition-colors ${viewMode === 'grid' ? 'border-primary bg-primary/10 text-primary' : 'border-gray-300 hover:bg-gray-100 text-gray-600'}`}
                title="Grid view"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
          
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-gray-600">Loading articles...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-500 text-xl">No blog posts available yet. Check back soon!</p>
            </div>
          ) : (
            <>
              <div className={viewMode === 'grid' 
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8' 
                : 'flex flex-col gap-6'}>
                {posts.map((post, index) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className={`bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 group ${
                      viewMode === 'grid' ? 'flex flex-col h-full' : 'flex flex-row'
                    }`}
                    data-aos="fade-up"
                    data-aos-delay={index * 100}
                  >
                    {/* Image */}
                    <div className={`relative overflow-hidden ${
                      viewMode === 'grid' ? 'h-56 w-full' : 'h-48 w-64 flex-shrink-0'
                    }`}>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity z-10"></div>
                      <img 
                        src={(() => {
                          const img = post.featured_image || post.image;
                          if (img && !img.startsWith('http') && !img.startsWith('data:')) {
                            return `http://localhost/karnidigitalsolutions/backend/public/storage/${img}`;
                          }
                          return img || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iODAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IiM5Y2EzYWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5CbG9nIFBvc3QgSW1hZ2U8L3RleHQ+PC9zdmc+'; 
                        })()} 
                        alt={post.title}
                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iODAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IiM5Y2EzYWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5CbG9nIFBvc3QgSW1hZ2U8L3RleHQ+PC9zdmc+';
                        }}
                      />
                      <div className="absolute top-4 left-4 z-20">
                        <span className="bg-primary/90 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-medium">
                          {post.category?.name || 'Uncategorized'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="p-6 flex-1 flex flex-col">
                      {/* Date and Reading Time */}
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-primary" />
                          {new Date(post.created_at || post.publishedAt || Date.now()).toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4 text-primary" />
                          <span>{(post.readTime ?? 5)} min read</span>
                        </div>
                      </div>

                      {/* Title */}
                      <h2 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-primary transition-colors">
                        {post.title}
                      </h2>

                      {/* Excerpt */}
                      <p className="text-gray-600 mb-6 line-clamp-3 flex-1">
                        {post.excerpt}
                      </p>

                      {/* Author */}
                      <div className="flex items-center gap-3 mb-4 pt-4 border-t border-gray-100">
                        <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white text-sm font-bold">
                          {(post.author?.name || post.author || 'Admin')[0].toUpperCase()}
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-900">By {post.author?.name || post.author || 'Admin'}</span>
                        </div>
                      </div>

                      {/* Tags */}
                      {post.tags && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {(() => {
                            // Ensure post.tags is an array
                            const tagsArray = Array.isArray(post.tags) 
                              ? post.tags 
                              : typeof post.tags === 'string' 
                                ? post.tags.split(',').map(tag => tag.trim()) 
                                : [];
                            
                            return tagsArray.slice(0, 2).map((tag, idx) => (
                              <span key={idx} className="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded-full text-xs transition-colors">
                                <Tag className="w-3 h-3" />
                                {tag}
                              </span>
                            ));
                          })()}
                        </div>
                      )}

                      {/* Read More */}
                      <div className="flex items-center justify-between mt-auto pt-4">
                        <span className="text-primary font-medium group-hover:text-primary-600 transition-colors text-sm flex items-center">
                          Read Full Article <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </span>
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          {Math.floor(Math.random() * 1000) + 100}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-12 flex justify-center items-center">
                  <nav className="flex items-center gap-2">
                    <button 
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`p-2 rounded-lg border ${currentPage === 1 ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300 hover:bg-gray-100 text-gray-700'} transition-colors`}
                      aria-label="Previous page"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    
                    {/* Page Numbers */}
                    {[...Array(totalPages)].map((_, index) => {
                      const pageNumber = index + 1;
                      // Show current page, first, last, and pages around current
                      if (
                        pageNumber === 1 || 
                        pageNumber === totalPages || 
                        (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => handlePageChange(pageNumber)}
                            className={`w-10 h-10 rounded-lg ${pageNumber === currentPage 
                              ? 'bg-primary text-white font-medium' 
                              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'} transition-colors`}
                            aria-label={`Page ${pageNumber}`}
                            aria-current={pageNumber === currentPage ? 'page' : undefined}
                          >
                            {pageNumber}
                          </button>
                        );
                      } else if (
                        (pageNumber === 2 && currentPage > 3) || 
                        (pageNumber === totalPages - 1 && currentPage < totalPages - 2)
                      ) {
                        // Show ellipsis
                        return <span key={pageNumber} className="px-2">...</span>;
                      }
                      return null;
                    })}
                    
                    <button 
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`p-2 rounded-lg border ${currentPage === totalPages ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300 hover:bg-gray-100 text-gray-700'} transition-colors`}
                      aria-label="Next page"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </nav>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-gold">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-primary mb-4" data-aos="fade-up">
            Need Expert Help with Your Digital Project?
          </h2>
          <p className="text-xl text-primary-600 mb-8" data-aos="fade-up" data-aos-delay="200">
            Get a FREE consultation from Pattaya's leading software development company
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-aos="fade-up" data-aos-delay="400">
            <Link to="/contact" className="btn-primary">
              Get Free Quote
            </Link>
            <a href="https://wa.me/66968826252" target="_blank" rel="noopener noreferrer" className="btn-gold">
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogList;
